/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useMemo, useRef, MutableRef, Ref } from 'preact/hooks';
import { useCollectionFocusRing } from '../../hooks/PRIVATE_useCollectionFocusRing';
import { useTabbableModeSet } from '../../hooks/PRIVATE_useTabbableModeSet';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { Keys } from '../../utils/UNSAFE_keys';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { ColumnResizeInfo, SizingInfo, TableColumn } from '../Table';
import { handleRowActionGesture, handleSortActionGesture } from '../utils/TableActionUtils';
import { getElementFromCell, logicalCellExtractor } from '../utils/TableDomUtils';
import {
  cellComparator,
  handleElementFocus,
  handleAdjacentRowGesture,
  handleAdjacentColumnGesture,
  handlePageRowGesture,
  handleJumpColumnGesture
} from '../utils/TableFocusUtils';
import {
  handleSelectionGesture,
  handleRangeSelectionGesture,
  handleExtendSelectionGesture,
  isRangeSelectionGesture
} from '../utils/TableSelectionUtils';
import {
  handleOverResizeRegionGesture,
  handleColumnResizeStartGesture,
  handleColumnResizingGesture,
  handleColumnResizeEndGesture,
  handleLeaveResizingGesture,
  handleEnterResizingGesture
} from '../utils/TableSizingUtils';
import { updateStickyEdges } from '../utils/TableScrollUtils';
import { SelectionMode } from '../../UNSAFE_Collection';
import {
  Cell,
  ColumnWidths,
  CurrentCellDetail,
  RowActionDetail,
  SortCriterionDetail,
  ColumnResizeDetail,
  TableSelectionDetail
} from '../../UNSAFE_TableView';

type UseInteractionManagerOptions<K, D, C extends string> = {
  outerTableRef: Ref<HTMLDivElement>;
  tableId: string;
  data: D[] | null;
  isRtl: boolean;
  columnsArray: TableColumn<K, D, C>[];
  appliedColumnWidths: ColumnWidths<C>;
  sizingInfoRef: MutableRef<SizingInfo>;
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>;
  currentStickyEdgesRef: Ref<{ start?: number; end?: number }>;
  cellHasRenderer: (currentCell: Cell<K, C>) => boolean;
  fullColumnWidths?: number[];
  onColumnResizing?: (detail: ColumnResizeDetail<C>) => void;
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void;
  currentCell?: Cell<K, C>;
  onCurrentCellChange?: (detail: CurrentCellDetail<K, C>) => void;
  selectionMode?: { row?: SelectionMode; column?: SelectionMode };
  selected?: { row?: Keys<K>; column?: Keys<C> };
  onSelectionChange?: (detail: TableSelectionDetail<K, C>) => void;
  onRowAction?: (detail: RowActionDetail<K, D>) => void;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
};

/**
 * Hook that manages interactions and events on the Preact Table
 */
export function useInteractionManager<K extends string | number, D, C extends string>({
  outerTableRef,
  tableId,
  data,
  isRtl,
  columnsArray,
  appliedColumnWidths,
  sizingInfoRef,
  resizeInfoRef,
  currentStickyEdgesRef,
  cellHasRenderer,
  fullColumnWidths,
  onColumnResizing,
  onColumnResize,
  currentCell,
  onCurrentCellChange,
  selectionMode,
  selected,
  onSelectionChange,
  onRowAction,
  sortCriterion,
  onSortCriterionChange
}: UseInteractionManagerOptions<K, D, C>) {
  // tracks whether the most recent 'blur' was caused by focus being lost by the browser window (or iframe)
  const blurFromWindowRef = useRef<boolean>(false);

  // the logical cell targeted via focus that should become 'current'
  const pendingCurrentCellRef = useRef<Cell<K, C>>();

  // tracks whether the most recent 'pointerdown' event was due to a touch gesture
  const selectionTouchRef = useRef<boolean>(false);

  // tracks whether the most recent 'click' gesture is part of a selection extension gesture
  const pendingSelectionExtensionRef = useRef<boolean>(false);

  // tracks the current selection extension element (if any)
  const selectionExtensionElementRef = useRef<HTMLElement>();

  // intercept onCurrentCellChange calls to ensure local selectionExtensionElementRef is cleared
  const onTableCurrentCellChange = useMemo(() => {
    return onCurrentCellChange != null
      ? (detail: CurrentCellDetail<K, C>) => {
          pendingSelectionExtensionRef.current = false;
          selectionExtensionElementRef.current = undefined;
          onCurrentCellChange(detail);
        }
      : undefined;
  }, [onCurrentCellChange]);

  // intercept onSelectionChange calls to ensure local selectionExtensionElementRef is cleared
  const onTableSelectionChange = useMemo(() => {
    return onSelectionChange != null
      ? (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => {
          if (!isExtendableSelection) {
            pendingSelectionExtensionRef.current = false;
            selectionExtensionElementRef.current = undefined;
          }
          onSelectionChange(detail);
        }
      : undefined;
  }, [onSelectionChange]);

  // setup tabbable mode tracking using the shared useTabbableModeSet hook
  const [isCurrentTabbableCell, tabbableModeProps, getCurrentTabbableCell, setCurrentTabbableCell] =
    useTabbableModeSet(
      outerTableRef,
      (element: HTMLElement) => {
        return logicalCellExtractor(
          element,
          tableId,
          `[data-oj-table-focusable='${tableId}']`,
          true
        ) as Cell<K, C>;
      },
      currentCell,
      onTableCurrentCellChange,
      cellHasRenderer,
      cellComparator
    );
  const isTabbable = !isCurrentTabbableCell(undefined);

  // setup focus ring management using the shared useCollectionFocusRing hook
  const [showFocusRing, focusRingProps] = useCollectionFocusRing(outerTableRef, [
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'PageUp',
    'PageDown',
    'Home',
    'End'
  ]);

  // Blur handler for the outer Table. When a blur is caused by the browser window itself
  // losing focus (changing tabs or leaving an iframe for example), an eventual re-focus due
  // to the window re-gaining focus needs to be handled differently (no auto-scroll mostly).
  const _blurHandler = useCallback((event: FocusEvent) => {
    blurFromWindowRef.current = event.relatedTarget == null;
  }, []);

  // Focus handler for the outer Table. On focus, a focusable area needs to be 'current'.
  // If a 'current' area already exists, nothing further is needed. Otherwise, the first
  // focusable element should be made 'current'.
  const _focusHandler = useCallback(
    (event: FocusEvent) => {
      const targetElement = event.target as HTMLElement;
      if (outerTableRef.current != null && targetElement != null) {
        handleElementFocus(
          outerTableRef.current,
          tableId,
          targetElement,
          columnsArray,
          sizingInfoRef,
          pendingCurrentCellRef,
          blurFromWindowRef,
          pendingSelectionExtensionRef,
          isRtl,
          currentCell,
          onTableCurrentCellChange,
          fullColumnWidths
        );
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      sizingInfoRef,
      isRtl,
      currentCell,
      onTableCurrentCellChange,
      fullColumnWidths
    ]
  );

  // Pointer move handler for the outer Table.
  const _pointerMoveHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null && onColumnResizing != null) {
        if (!resizeInfoRef.current.isResizing) {
          handleOverResizeRegionGesture(
            outerTableRef.current,
            tableId,
            event,
            columnsArray,
            resizeInfoRef,
            isRtl
          );
        } else {
          handleColumnResizingGesture(
            outerTableRef.current,
            tableId,
            event,
            appliedColumnWidths,
            resizeInfoRef,
            isRtl,
            fullColumnWidths,
            onColumnResizing
          );
        }
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      appliedColumnWidths,
      resizeInfoRef,
      isRtl,
      fullColumnWidths,
      onColumnResizing
    ]
  );

  // Pointer down handler for the outer Table. It ensures the area targeted is 'current'.
  // NOTE - This event handling is triggered prior to 'onFocus' handling and ensures the
  // 'pending current' area is set when an initial pointer interaction focuses the Table.
  const _pointerDownHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null) {
        selectionTouchRef.current = event.pointerType === 'touch';
        let isSelectionExtension = false;
        const target = event.target as HTMLElement;
        if (onTableSelectionChange != null) {
          if (currentCell != null && event.shiftKey) {
            isSelectionExtension = isRangeSelectionGesture(
              outerTableRef.current,
              tableId,
              target,
              currentCell,
              selectionMode
            );
            pendingSelectionExtensionRef.current = isSelectionExtension;
          }
        }
        if (!isSelectionExtension) {
          if (
            handleColumnResizeStartGesture(
              outerTableRef.current,
              tableId,
              event,
              columnsArray,
              appliedColumnWidths,
              resizeInfoRef,
              isRtl,
              fullColumnWidths,
              onColumnResizing
            )
          ) {
            resizeInfoRef.current.ignoreClick = true;
          }
        }
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      appliedColumnWidths,
      resizeInfoRef,
      isRtl,
      currentCell,
      selectionMode,
      onTableSelectionChange,
      fullColumnWidths,
      onColumnResizing
    ]
  );

  // Pointer up handler for the outer Table.
  const _pointerUpHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null) {
        handleColumnResizeEndGesture(
          outerTableRef.current,
          tableId,
          event,
          appliedColumnWidths,
          resizeInfoRef,
          isRtl,
          onColumnResize
        );
      }
    },
    [outerTableRef, tableId, appliedColumnWidths, resizeInfoRef, isRtl, onColumnResize]
  );

  // Pointer leave handler for the outer Table.
  const _pointerLeaveHandler = useCallback(
    (_event: PointerEvent) => {
      if (outerTableRef.current != null) {
        handleLeaveResizingGesture(outerTableRef.current, tableId, resizeInfoRef);
      }
    },
    [outerTableRef, tableId, resizeInfoRef]
  );

  // Pointer leave handler for the outer Table.
  const _pointerEnterHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null) {
        handleEnterResizingGesture(
          outerTableRef.current,
          tableId,
          event,
          appliedColumnWidths,
          resizeInfoRef,
          isRtl,
          onColumnResize
        );
      }
    },
    [outerTableRef, tableId, appliedColumnWidths, resizeInfoRef, isRtl, onColumnResize]
  );

  // Click handler for the outer Table. It ensures the Table's selection state is
  // updated to reflect the area targeted.
  const _clickHandler = useCallback(
    (event: MouseEvent) => {
      let isSelectionExtension = false;
      const platform = getClientHints().platform;
      const target = event.target as HTMLElement;
      if (outerTableRef.current != null) {
        if (onTableSelectionChange != null && !resizeInfoRef.current.ignoreClick) {
          if (currentCell != null && event.shiftKey) {
            isSelectionExtension = handleRangeSelectionGesture(
              outerTableRef.current,
              tableId,
              columnsArray,
              target,
              currentCell,
              selectionExtensionElementRef,
              onTableSelectionChange,
              selectionMode,
              selected
            );
          }
          if (!isSelectionExtension) {
            handleSelectionGesture(
              outerTableRef.current,
              tableId,
              columnsArray,
              target,
              (platform === 'mac' ? event.metaKey : event.ctrlKey) || selectionTouchRef.current,
              onTableSelectionChange,
              selectionMode,
              selected
            );
          }
        }
        if (
          onRowAction != null &&
          data != null &&
          handleRowActionGesture(outerTableRef.current, tableId, data, onRowAction, target)
        ) {
          event.stopPropagation();
        }
      }
      resizeInfoRef.current.ignoreClick = false;
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      data,
      resizeInfoRef,
      selectionExtensionElementRef,
      currentCell,
      selectionMode,
      selected,
      onTableSelectionChange,
      onRowAction
    ]
  );

  // Keydown handler for the outer Table. This handles all internal
  // keyboard navigation for the Table.
  const _keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const platform = getClientHints().platform;
      const keyHandlers: Record<string, (root: HTMLDivElement) => void> = {
        ' ': function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (currentCell != null) {
              if (!isTabbable && onTableSelectionChange != null) {
                handleSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  getElementFromCell(root, tableId, currentCell) as HTMLElement,
                  true,
                  onTableSelectionChange,
                  selectionMode,
                  selected
                );
              }
              if (
                onRowAction != null &&
                data != null &&
                handleRowActionGesture(
                  outerTableRef.current,
                  tableId,
                  data,
                  onRowAction,
                  event.target as HTMLElement,
                  currentCell
                )
              ) {
                event.stopPropagation();
              }
            }
            event.preventDefault();
          }
        },
        Enter: function () {
          if (outerTableRef.current != null) {
            if (currentCell != null) {
              if (
                onRowAction != null &&
                data != null &&
                currentCell.type === 'data' &&
                handleRowActionGesture(
                  outerTableRef.current,
                  tableId,
                  data,
                  onRowAction,
                  event.target as HTMLElement,
                  currentCell
                )
              ) {
                event.stopPropagation();
              } else if (
                !isTabbable &&
                onSortCriterionChange != null &&
                currentCell.type === 'header' &&
                handleSortActionGesture(
                  columnsArray,
                  currentCell.columnKey,
                  onSortCriterionChange,
                  sortCriterion
                )
              ) {
                event.stopPropagation();
              }
            }
            event.preventDefault();
          }
        },
        ArrowUp: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
              if (
                currentCell.type === 'data' &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.row === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  true,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentRowGesture(
                  root,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  currentCell,
                  onTableCurrentCellChange,
                  true,
                  isRtl,
                  fullColumnWidths
                );
              }
            }
            event.preventDefault();
          }
        },
        ArrowDown: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
              if (
                currentCell.type === 'data' &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.row === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  false,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentRowGesture(
                  root,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  currentCell,
                  onTableCurrentCellChange,
                  false,
                  isRtl,
                  fullColumnWidths
                );
              }
            }
            event.preventDefault();
          }
        },
        ArrowLeft: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
              if (
                (currentCell.type === 'header' || currentCell.type === 'footer') &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.column === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  true,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentColumnGesture(
                  root,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  currentCell,
                  onTableCurrentCellChange,
                  !isRtl,
                  isRtl,
                  fullColumnWidths
                );
              }
            }
            event.preventDefault();
          }
        },
        ArrowRight: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
              if (
                (currentCell.type === 'header' || currentCell.type === 'footer') &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.column === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  false,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentColumnGesture(
                  root,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  currentCell,
                  onTableCurrentCellChange,
                  isRtl,
                  isRtl,
                  fullColumnWidths
                );
              }
            }
            event.preventDefault();
          }
        },
        PageUp: function (root: HTMLDivElement) {
          if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
            handlePageRowGesture(
              root,
              tableId,
              columnsArray,
              sizingInfoRef,
              currentCell,
              onTableCurrentCellChange,
              true,
              isRtl,
              fullColumnWidths
            );
          }
          event.preventDefault();
        },
        PageDown: function (root: HTMLDivElement) {
          if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
            handlePageRowGesture(
              root,
              tableId,
              columnsArray,
              sizingInfoRef,
              currentCell,
              onTableCurrentCellChange,
              false,
              isRtl,
              fullColumnWidths
            );
          }
          event.preventDefault();
        },
        Home: function (root: HTMLDivElement) {
          if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
            handleJumpColumnGesture(
              root,
              tableId,
              columnsArray,
              sizingInfoRef,
              currentCell,
              onTableCurrentCellChange,
              true,
              platform === 'mac' ? event.metaKey : event.ctrlKey,
              isRtl,
              fullColumnWidths
            );
          }
          event.preventDefault();
        },
        End: function (root: HTMLDivElement) {
          if (!isTabbable && currentCell != null && onTableCurrentCellChange != null) {
            handleJumpColumnGesture(
              root,
              tableId,
              columnsArray,
              sizingInfoRef,
              currentCell,
              onTableCurrentCellChange,
              false,
              platform === 'mac' ? event.metaKey : event.ctrlKey,
              isRtl,
              fullColumnWidths
            );
          }
          event.preventDefault();
        }
      };
      if (Object.keys(keyHandlers).includes(event.key) && outerTableRef.current != null) {
        keyHandlers[event.key](outerTableRef.current);
      }
    },
    [
      outerTableRef,
      tableId,
      data,
      isRtl,
      isTabbable,
      columnsArray,
      sizingInfoRef,
      selectionExtensionElementRef,
      fullColumnWidths,
      currentCell,
      onTableCurrentCellChange,
      selectionMode,
      selected,
      onTableSelectionChange,
      onRowAction,
      sortCriterion,
      onSortCriterionChange
    ]
  );

  // Scroll hander for the outer Table. It ensures the Table's sticky edges are updated.
  const _scrollHandler = useCallback(() => {
    if (outerTableRef.current != null && fullColumnWidths != null) {
      updateStickyEdges(
        outerTableRef.current,
        tableId,
        isRtl,
        columnsArray,
        fullColumnWidths,
        currentStickyEdgesRef,
        outerTableRef.current.scrollLeft
      );
    }
  }, [outerTableRef, tableId, isRtl, columnsArray, currentStickyEdgesRef, fullColumnWidths]);

  const interactionManagerProps = mergeProps(tabbableModeProps, focusRingProps, {
    onBlur: _blurHandler,
    onFocus: _focusHandler,
    onPointerMove: _pointerMoveHandler,
    onPointerDown: _pointerDownHandler,
    onPointerUp: _pointerUpHandler,
    onPointerLeave: _pointerLeaveHandler,
    onPointerEnter: _pointerEnterHandler,
    onClick: _clickHandler,
    onKeyDown: _keyDownHandler,
    onScroll: _scrollHandler
  });

  return {
    interactionManagerProps,
    onTableSelectionChange,
    showFocusRing,
    isCurrentTabbableCell,
    getCurrentTabbableCell,
    setCurrentTabbableCell
  };
}
